const KNOWN_DOMAINS = ['sandbox4ever.com', 'staging4ever.com', 'forever.com'];

export const AUTH_PATHS = {
  signIn: '/sign_in',
  cognitoSignIn: '/auth/cognito-idp',
  signOut: '/sign_out',
};

function resolveHost(hostname) {
  for (const domain of KNOWN_DOMAINS) {
    if (hostname === domain || hostname.endsWith(`.${domain}`)) {
      return `https://auth.${domain}`;
    }
  }
  return null;
}

export function useAuthHost() {
  const runtimeConfig = useRuntimeConfig();
  const {public: {apiAuthHost, appBaseUrl, serverEnv}} = runtimeConfig;
  const requestUrl = useRequestURL();
  const host = apiAuthHost || resolveHost(requestUrl.hostname) || '/auth';

  function getAppUrl() {
    if (requestUrl.pathname.endsWith(AUTH_PATHS.signIn)) {
      const queryOrigin = new URLSearchParams(requestUrl.search).get('origin');
      if (queryOrigin) return queryOrigin;
    }

    return appBaseUrl
      ? `${appBaseUrl}${requestUrl.pathname}${requestUrl.search}`
      : requestUrl.href;
  }

  function getSharedSignInBase() {
    const base = new URL(appBaseUrl || requestUrl.origin, requestUrl.origin);
    base.pathname = AUTH_PATHS.signIn;
    base.search = '';
    base.hash = '';
    return base.toString().replace(/\/$/, '');
  }

  function getLocalSignInBase() {
    const appPath = (runtimeConfig.app.baseURL ?? '/').replace(/\/$/, '');
    const base = new URL(requestUrl.origin);
    base.pathname = `${appPath}${AUTH_PATHS.signIn}`;
    base.search = '';
    base.hash = '';
    return base.toString().replace(/\/$/, '');
  }

  function shouldUseLocalSignInFallback() {
    const hostname = requestUrl.hostname;
    return serverEnv === 'local' ||
      hostname === 'localhost' ||
      hostname.endsWith('.dev.localhost') ||
      hostname.startsWith('f3dev.');
  }

  function buildUrlWithOrigin(base, originUrl) {
    const origin = originUrl ?? (appBaseUrl ? `${appBaseUrl}${requestUrl.pathname}${requestUrl.search}` : requestUrl.href);
    return `${base}?origin=${encodeURIComponent(origin)}`;
  }

  function buildSignInUrl(originUrl) {
    const base = shouldUseLocalSignInFallback()
      ? getLocalSignInBase()
      : getSharedSignInBase();
    return buildUrlWithOrigin(base, originUrl ?? getAppUrl());
  }

  function buildCognitoSignInUrl(originUrl) {
    return buildUrlWithOrigin(`${host}${AUTH_PATHS.cognitoSignIn}`, originUrl ?? getAppUrl());
  }

  // Local dev only: return to this host's local sign-in page after sign-out instead of the auth
  // service's default landing (www). Deployed apps always get the bare sign-out URL.
  function buildSignOutUrl() {
    const signOutUrl = `${host}${AUTH_PATHS.signOut}`;
    if (!shouldUseLocalSignInFallback()) return signOutUrl;
    return `${signOutUrl}?redirect_url=${encodeURIComponent(getLocalSignInBase())}`;
  }

  return {
    host,
    signInUrl: getSharedSignInBase(),
    cognitoSignInUrl: `${host}${AUTH_PATHS.cognitoSignIn}`,
    signOutUrl: `${host}${AUTH_PATHS.signOut}`,
    getAppUrl,
    buildSignInUrl,
    buildCognitoSignInUrl,
    buildSignOutUrl,
  };
}
